/**
 * Free Gift — minimum-spend picker (all paid products qualify)
 *
 * Modal shows when cart subtotal >= minimum and no managed gift yet.
 * "No Thanks" only dismisses until cart changes or a new page load.
 */
(function () {
  const CFG = window.__FREE_GIFT_CONFIG;
  if (!CFG || !window.Shopify?.routes?.root) return;

  const root = window.Shopify.routes.root;
  const GIFT_PROP = '_free_gift';
  const GIFT_TITLE_PROP = '_free_gift_title';
  const offerEnabled = CFG.enabled !== false;
  const log = (...args) => { if (CFG.debug) console.log('[FreeGift]', ...args); };

  let isReconciling = false;
  let promoInactive = false;
  let modalSuppressed = false;   // in-memory only — resets on page load
  let lastCartFingerprint = '';
  let timer = null;
  let modalEl = null;
  let selectedVariantId = null;

  const giftVariantIdSet = new Set(CFG.giftVariantIds || []);
  const giftProductIdSet = new Set(CFG.giftProductIds || []);

  const isCartPage = () => window.themeVariables?.settings?.pageType === 'cart';
  const getCart = async () => (await fetch(root + 'cart.js')).json();

  function cartFingerprint(cart) {
    return (cart.items || [])
      .map((i) => `${i.variant_id}:${i.quantity}`)
      .sort()
      .join('|');
  }

  function isGiftLine(item) {
    // Only $0 picker variants with the managed property count as the free gift
    return giftVariantIdSet.has(item.variant_id) && item.properties && item.properties[GIFT_PROP];
  }

  function isAnyGiftVariant(item) {
    // Whole gift product (incl. paid CRM/SAP variants) is blocked from staying in cart unmanaged
    return giftVariantIdSet.has(item.variant_id) || giftProductIdSet.has(item.product_id);
  }

  function isQualifyingItem(item) {
    if (isGiftLine(item)) return false;
    if (isAnyGiftVariant(item)) return false;
    return true;
  }

  function qualifyingSubtotal(cart) {
    let total = 0;
    for (const item of cart.items) {
      if (isQualifyingItem(item)) total += item.final_line_price;
    }
    return total;
  }

  function hasManagedGift(cart) {
    return cart.items.some(isGiftLine);
  }

  function collectSections() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );
    return sections.join(',');
  }

  async function mutate(endpoint, body) {
    const res = await fetch(root + endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    });
    if (!res.ok) console.warn('[FreeGift] cart API error', endpoint, res.status);
    return res.json();
  }

  async function renderCart() {
    return mutate('cart/update.js', { updates: {}, sections: collectSections() });
  }

  function dispatchChange(cart, baseEvent) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent, cart } })
    );
  }

  async function removeGiftLine(giftLine) {
    if (!giftLine) return getCart();
    return mutate('cart/change.js', { id: giftLine.key, quantity: 0 });
  }

  let allowGiftAdd = false;

  async function addGift(variantId) {
    if (!giftVariantIdSet.has(variantId)) {
      console.warn('[FreeGift] refused non-$0 gift variant', variantId);
      return getCart();
    }
    allowGiftAdd = true;
    try {
      await mutate('cart/add.js', {
        items: [{
          id: variantId,
          quantity: 1,
          properties: {
            [GIFT_PROP]: 'true',
            [GIFT_TITLE_PROP]: CFG.promoTitle || ''
          }
        }]
      });
    } finally {
      allowGiftAdd = false;
    }
    return getCart();
  }

  // Block direct /cart/add of gift SKUs (permalink, console, scrapers) except our picker
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async function (input, init) {
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      if (/\/cart\/add(?:\.js)?/i.test(url) && !allowGiftAdd) {
        let bodyText = '';
        if (init && typeof init.body === 'string') bodyText = init.body;
        else if (init && init.body && typeof init.body === 'object' && typeof init.body.get === 'function') {
          bodyText = init.body.get('id') || '';
        }
        const ids = [];
        try {
          const parsed = bodyText && bodyText.trim().startsWith('{') ? JSON.parse(bodyText) : null;
          if (parsed?.items) parsed.items.forEach((it) => ids.push(Number(it.id)));
          else if (parsed?.id) ids.push(Number(parsed.id));
        } catch (_) { /* form-encoded */ }
        const formId = bodyText && /(?:^|&)id=(\d+)/.exec(bodyText);
        if (formId) ids.push(Number(formId[1]));
        if (ids.some((id) => giftVariantIdSet.has(id))) {
          console.warn('[FreeGift] blocked unauthorized gift add', ids);
          schedule();
          return new Response(JSON.stringify({ status: 422, message: 'Gift items can only be added via the free gift offer', description: 'Gift items can only be added via the free gift offer' }), {
            status: 422,
            headers: { 'Content-Type': 'application/json' }
          });
        }
      }
    } catch (e) {
      console.warn('[FreeGift] fetch guard error', e);
    }
    return nativeFetch(input, init);
  };

  function getModal() {
    if (!modalEl) modalEl = document.getElementById('free-gift-modal');
    return modalEl;
  }

  function bindModal() {
    const modal = getModal();
    if (!modal || modal.__freeGiftBound) return;
    modal.__freeGiftBound = true;

    modal.querySelector('.free-gift-modal__close')?.addEventListener('click', () => hideModal(true));
    modal.querySelector('.free-gift-modal__overlay')?.addEventListener('click', () => hideModal(true));
    modal.querySelector('.free-gift-modal__decline-btn')?.addEventListener('click', () => hideModal(true));

    modal.querySelector('.free-gift-modal__add-btn')?.addEventListener('click', async () => {
      if (!selectedVariantId || isReconciling) return;
      isReconciling = true;
      setAddButtonLoading(true);
      try {
        await addGift(selectedVariantId);
        modalSuppressed = false;
        hideModal(false);
        await refreshUI('line-item:change');
      } catch (e) {
        console.error('[FreeGift] add gift error:', e);
      } finally {
        isReconciling = false;
        setAddButtonLoading(false);
      }
    });

    modal.querySelectorAll('.free-gift-modal__option').forEach((option) => {
      option.addEventListener('click', () => selectGiftOption(option));
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('show')) hideModal(true);
    });
  }

  function selectGiftOption(option) {
    const modal = getModal();
    if (!modal) return;
    modal.querySelectorAll('.free-gift-modal__option').forEach((el) => el.classList.remove('selected'));
    option.classList.add('selected');
    selectedVariantId = parseInt(option.dataset.variantId, 10);
    const addBtn = modal.querySelector('.free-gift-modal__add-btn');
    if (addBtn) addBtn.disabled = !selectedVariantId;
  }

  function setAddButtonLoading(loading) {
    const modal = getModal();
    if (!modal) return;
    const addBtn = modal.querySelector('.free-gift-modal__add-btn');
    const btnText = addBtn?.querySelector('.btn-text');
    const btnLoading = addBtn?.querySelector('.btn-loading');
    if (!addBtn) return;
    addBtn.disabled = loading || !selectedVariantId;
    if (btnText) btnText.style.display = loading ? 'none' : '';
    if (btnLoading) btnLoading.style.display = loading ? 'inline' : 'none';
  }

  function showModal() {
    const modal = getModal();
    if (!modal) return;
    bindModal();

    selectedVariantId = null;
    modal.querySelectorAll('.free-gift-modal__option').forEach((el) => el.classList.remove('selected'));
    const addBtn = modal.querySelector('.free-gift-modal__add-btn');
    if (addBtn) addBtn.disabled = true;

    modal.style.display = 'flex';
    requestAnimationFrame(() => modal.classList.add('show'));
    document.body.style.overflow = 'hidden';
  }

  function hideModal(suppress) {
    const modal = getModal();
    if (!modal) return;
    if (suppress) modalSuppressed = true;
    modal.classList.remove('show');
    document.body.style.overflow = '';
    setTimeout(() => { modal.style.display = 'none'; }, 300);
  }

  function shouldShowModal(cart) {
    if (modalSuppressed) return false;
    if (hasManagedGift(cart)) return false;
    if (qualifyingSubtotal(cart) < CFG.minimumAmount) return false;
    if (promoInactive) return false;
    return true;
  }

  function updateCartFingerprint(cart) {
    const fp = cartFingerprint(cart);
    if (fp !== lastCartFingerprint) {
      modalSuppressed = false;
      lastCartFingerprint = fp;
    }
  }

  async function syncGift(cart) {
    let changed = false;

    // 1) Strip any gift-product line that is not a legitimate single $0 picker gift
    for (const item of cart.items) {
      if (!isAnyGiftVariant(item)) continue;
      const managed = item.properties && item.properties[GIFT_PROP];
      const isZeroPickerVariant = giftVariantIdSet.has(item.variant_id) && item.final_line_price === 0;
      // Paid CRM variants, unmanaged adds, or missing property → remove
      if (!managed || !isZeroPickerVariant) {
        await mutate('cart/change.js', { id: item.key, quantity: 0 });
        changed = true;
      }
    }
    if (changed) cart = await getCart();

    // 2) Cap every managed gift line at qty 1 (blocks /cart/VARIANT:12 abuse)
    for (const item of cart.items) {
      if (!isGiftLine(item)) continue;
      if (item.quantity > 1) {
        await mutate('cart/change.js', { id: item.key, quantity: 1 });
        changed = true;
      }
    }
    if (changed) cart = await getCart();

    // Offer disabled → remove remaining managed gifts too
    if (!offerEnabled) {
      for (const item of cart.items) {
        if (!isGiftLine(item)) continue;
        await mutate('cart/change.js', { id: item.key, quantity: 0 });
        changed = true;
      }
      return changed;
    }

    updateCartFingerprint(cart);

    const subtotal = qualifyingSubtotal(cart);
    const qualifies = subtotal >= CFG.minimumAmount;
    const giftLine = cart.items.find(isGiftLine);
    log({ subtotal, qualifies, hasGift: !!giftLine, giftQty: giftLine?.quantity, suppressed: modalSuppressed });

    // 3) Below threshold → gift must not remain (also blocks "gift-only" checkout)
    if (!qualifies) {
      promoInactive = false;
      modalSuppressed = false;
      if (giftLine) {
        await removeGiftLine(giftLine);
        changed = true;
      }
      return changed;
    }

    // Safety: never keep a gift that prices above $0
    if (giftLine && giftLine.final_line_price > 0) {
      promoInactive = true;
      await removeGiftLine(giftLine);
      changed = true;
      return changed;
    }

    if (giftLine) {
      modalSuppressed = false;
      return changed;
    }

    if (shouldShowModal(cart)) {
      showModal();
    }

    return changed;
  }

  async function refreshUI(baseEvent) {
    if (isCartPage()) { window.location.reload(); return; }
    const cart = await renderCart();
    if (cart && cart.sections) dispatchChange(cart, baseEvent || 'line-item:change');
  }

  async function reconcile() {
    if (isReconciling) return;
    isReconciling = true;
    try {
      const changed = await syncGift(await getCart());
      if (changed) await refreshUI('line-item:change');
    } catch (e) {
      console.error('[FreeGift] reconcile error:', e);
    } finally {
      isReconciling = false;
    }
  }

  function schedule() {
    if (isReconciling) return;
    clearTimeout(timer);
    timer = setTimeout(reconcile, 80);
  }

  function readChangeIntent(event) {
    const link = event.type === 'click' && event.target.closest
      ? event.target.closest('a[href*="/cart/change"]') : null;
    if (link) {
      const url = new URL(link.href, window.location.origin);
      return { lineKey: url.searchParams.get('id'), quantity: parseInt(url.searchParams.get('quantity'), 10) };
    }
    if (event.type === 'change' && event.target.matches && event.target.matches('input[data-line-key]')) {
      return { lineKey: event.target.getAttribute('data-line-key'), quantity: parseInt(event.target.value, 10) };
    }
    return null;
  }

  function onCartPageChange(event) {
    const intent = readChangeIntent(event);
    if (!intent || !intent.lineKey || Number.isNaN(intent.quantity)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    applyCartPageChange(intent);
  }

  async function applyCartPageChange(intent) {
    isReconciling = true;
    try {
      await mutate('cart/change.js', { id: intent.lineKey, quantity: intent.quantity });
      await syncGift(await getCart());
    } catch (e) {
      console.error('[FreeGift] cart-page sync error:', e);
    } finally {
      window.location.reload();
    }
  }

  let checkoutSyncing = false;
  async function onCheckoutClick(event) {
    const ctrl = event.target?.closest?.('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]');
    if (!ctrl || ctrl.__freeGiftReady) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutSyncing) return;
    checkoutSyncing = true;
    try { await syncGift(await getCart()); }
    catch (e) { console.error('[FreeGift] pre-checkout sync error:', e); }
    finally {
      ctrl.__freeGiftReady = true;
      checkoutSyncing = false;
      if (ctrl.tagName === 'A') window.location.assign(ctrl.href);
      else ctrl.click();
    }
  }

  document.addEventListener('cart:change', schedule);
  document.addEventListener('click', onCheckoutClick, true);

  // Re-check when returning via back/forward cache or switching tabs
  window.addEventListener('pageshow', () => schedule());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') schedule();
  });

  if (isCartPage()) {
    document.addEventListener('click', onCartPageChange, true);
    document.addEventListener('change', onCartPageChange, true);
  }

  bindModal();
    // Check on every page load (not only cart changes)
  if (document.readyState !== 'loading') {
    schedule();
  } else {
    document.addEventListener('DOMContentLoaded', schedule);
  }
})();
